import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { contractDraftUrl, deleteDoc, errText, fetchDocs, openDoc, uploadDoc } from "@/lib/bf";
import BFLeadPick from "./BFLeadPick";

const DocList = ({ lead, testid }) => {
  const [rows, setRows] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = useCallback(() => fetchDocs(lead).then(setRows).catch(() => setRows([])), [lead]);
  useEffect(() => { load(); }, [load]);
  const up = (file) => {
    if (!file) return;
    setBusy(true);
    uploadDoc(file, title, lead).then(() => { toast.success("Document încărcat"); setTitle(""); load(); })
      .catch((e) => toast.error(errText(e, "Nu am putut încărca documentul"))).finally(() => setBusy(false));
  };
  const del = (d) => window.confirm(`Ștergi „${d.title}”?`) && deleteDoc(d.id).then(() => { toast.success("Document șters"); load(); }).catch((e) => toast.error(errText(e, "Nu am putut șterge")));
  return (
    <div className="bf-docs" data-testid={testid}>
      <div className="share-actions">
        <input className="bf-docs-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titlu (ex. Contract semnat 2026)" data-testid={`${testid}-title`} />
        <input ref={ref} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(e) => { up(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
        <button type="button" className="st-btn st-btn--light" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Încarcă document
        </button>
      </div>
      {rows && !rows.length && <p className="st-hint" data-testid={`${testid}-empty`}>Niciun document încă.</p>}
      <ul className="bfa-leads">
        {(rows || []).map((d) => (
          <li key={d.id} data-testid={`bf-doc-${d.id}`}>
            <div><b>{d.title}</b><span>{d.name} · {d.size < 1048576 ? `${Math.max(1, Math.round(d.size / 1024))} KB` : `${(d.size / 1048576).toFixed(1)} MB`} · {new Date(d.created_at).toLocaleDateString("ro-RO")} · {d.by}</span></div>
            <div className="interested-actions">
              <button type="button" className="st-btn st-btn--ghost" onClick={() => openDoc(d.id).catch(() => toast.error("Nu am putut deschide"))} data-testid={`bf-doc-open-${d.id}`}><FileText size={14} /> Deschide</button>
              <button type="button" className="st-btn st-btn--ghost" onClick={() => del(d)} aria-label="Șterge" data-testid={`bf-doc-del-${d.id}`}><Trash2 size={14} /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function BFDocs() {
  const [lead, setLead] = useState(null);
  return (
    <>
      <section className="invite-box" data-testid="bf-docs-general">
        <h3>Documente generale</h3>
        <p className="st-hint">Contracte-tip, regulamentul campaniei, nota GDPR și alte documente comune.</p>
        <div className="share-actions bf-drafts" data-testid="bf-contract-drafts">
          {[["vs", "VS Beauty Trends"], ["fb", "Fabrica de Beauty"], ["we", "W-Egan"]].map(([k, n]) => (
            <a key={k} className="st-btn st-btn--light" href={contractDraftUrl(k)} target="_blank" rel="noopener noreferrer" data-testid={`bf-contract-draft-${k}`}><Download size={14} /> Draft contract {n}</a>
          ))}
        </div>
        <DocList lead="" testid="bf-docs-gen" />
      </section>
      <section className="invite-box" data-testid="bf-docs-salon">
        <h3>Contracte pe salon</h3>
        <p className="st-hint">Caută salonul și încarcă contractul semnat (PDF sau poză). Rămâne legat de salon și îl poți deschide oricând.</p>
        <BFLeadPick value="" onPick={(l) => setLead(l)} />
        {lead && <DocList key={lead.id} lead={lead.id} testid="bf-docs-lead" />}
      </section>
    </>
  );
}
